/**
 * PIS Client Entry Point
 *
 * Bootstrap sequence:
 *   1. await loadConfig()        Fetches /config.json (runtime keys, highest priority)
 *   2. validateRequiredEnv()     Fail-fast check before mount
 *   3. Initialize i18n (async locale loading via Suspense)
 *   4. Mount React tree: StrictMode > ApolloProvider > AuthProvider > Router > App
 */

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { ApolloProvider } from '@apollo/client/react';
import { BrowserRouter } from 'react-router-dom';
import i18next from 'i18next';
import { loadConfig } from '@rtvloco/platform/env';
import { initAppTracing } from './observability/tracing';
import { validateRequiredEnv } from './config/envCheck';
import { themeManager } from '@rtvloco/theme/ThemeManager';
import { AuthProvider } from '@rtvloco/auth';
import { PreferencesProvider } from '@rtvloco/user-preferences';
// NOTE: ./preferences/setup is intentionally NOT statically imported here.
// It transitively imports ./auth/config (pisAuthManager → OidcAuthManager),
// whose module-level construction resolves the OIDC authority/clientId from
// runtime config. A static import would evaluate it during module load —
// BEFORE bootstrap()'s `await loadConfig()` — making the auth manager fall back
// to the build-time defaults (localhost:8180 / pis-client). It is loaded inside
// the post-loadConfig() dynamic batch in bootstrap() instead. Do not move it up.

import '@rtvloco/theme/base.css';
import '@rtvloco/theme/light.css';
import '@rtvloco/theme/dark.css';
import './styles/app-tokens.css';
import '@rtvloco/ui/styles';
import '@rtvloco/ui/shell/styles';
import './i18n';
import './styles/index.css';
import './App.css';
// NEXT-638 · UI-RESP-009 — additive mobile/touch layer; must load last so its
// media-gated rules win at equal specificity. Desktop (≥1024px) unaffected.
import './styles/mobile.css';

// ---------------------------------------------------------------------------
// Loading skeleton shown while i18n locale files load via Suspense
// ---------------------------------------------------------------------------

const AppLoadingSkeleton = () => (
  <div className="app-loading-skeleton">
    <div className="app-loading-spinner" aria-label="Loading application..." />
    <p className="app-loading-text">Loading SMARTTT PIS...</p>
  </div>
);

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

async function bootstrap() {
  await loadConfig();
  themeManager.initialize();
  initAppTracing();
  // Auth manager, apollo client, and preferences setup imported AFTER
  // loadConfig() so their module-level code (OidcAuthManager construction,
  // GraphQL URL resolution) runs with _config already populated. preferences/setup
  // is in this batch because it transitively imports ./auth/config — a static
  // import would construct the auth manager with build-time defaults before the
  // runtime /config.json is applied (see NEXT-61).
  const [
    { pisAuthManager },
    { pisApolloClient },
    { default: App },
    { preferencesConfig },
    { ApplicationErrorBoundary },
  ] = await Promise.all([
    import('./auth/config'),
    import('./graphql/client/pisApolloClient'),
    import('./App'),
    import('./preferences/setup'),
    import('./components/shared/ApplicationErrorBoundary'),
  ]);

  validateRequiredEnv();

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error(
      '[PIS] Root element #root not found. Ensure index.html contains <div id="root"></div>.',
    );
  }

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <Suspense fallback={<AppLoadingSkeleton />}>
        <ApplicationErrorBoundary>
          <AuthProvider manager={pisAuthManager}>
            <ApolloProvider client={pisApolloClient}>
              <PreferencesProvider config={preferencesConfig}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </PreferencesProvider>
            </ApolloProvider>
          </AuthProvider>
        </ApplicationErrorBoundary>
      </Suspense>
    </React.StrictMode>,
  );
}

bootstrap().catch(() => {
  const root = document.getElementById('root');
  if (!root) {
    return;
  }

  const alert = document.createElement('section');
  alert.className = 'error-boundary error-boundary--page';
  alert.setAttribute('role', 'alert');
  alert.setAttribute('aria-live', 'assertive');
  alert.tabIndex = -1;

  const content = document.createElement('div');
  content.className = 'error-boundary__content';
  const title = document.createElement('h1');
  title.className = 'error-boundary__title';
  title.textContent = i18next.t('common:error.boundary.title');
  const message = document.createElement('p');
  message.className = 'error-boundary__message';
  message.textContent = i18next.t('common:error.boundary.applicationMessage');
  const retry = document.createElement('button');
  retry.className = 'error-boundary__reset';
  retry.type = 'button';
  retry.textContent = i18next.t('common:error.boundary.reset');
  retry.addEventListener('click', () => window.location.reload());

  content.append(title, message, retry);
  alert.append(content);
  root.replaceChildren(alert);
  alert.focus();
});
